import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BadgeComponent,
  ButtonComponent,
  ColumnConfig,
  ColumnType,
  TableComponent
} from 'htx-ui-library';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { EmailTemplateService } from '../../../../../services/email-templates/email-template.service';
import { EmailTemplateListItem } from '../../../../../services/email-templates/email-template.model';

@Component({
  selector: 'app-email-template-overview-page',
  imports: [
    CommonModule,
    TranslocoDirective,
    TranslocoPipe,
    ButtonComponent,
    TableComponent,
    BadgeComponent,
    ProtectedContentComponent
  ],
  templateUrl: './email-template-overview-page.component.html',
  styleUrl: './email-template-overview-page.component.scss'
})
export class EmailTemplateOverviewPageComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private emailTemplateService = inject(EmailTemplateService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  private destroyRef = inject(DestroyRef);

  requiredRoles = this.navigationService.getAccessRoles('admin/email-templates');

  templates = signal<EmailTemplateListItem[]>([]);
  loading = signal<boolean>(false);
  columnConfig = signal<ColumnConfig<EmailTemplateListItem>[]>([]);

  statusTemplateRef = viewChild<TemplateRef<{ $implicit: EmailTemplateListItem }>>('statusTemplate');
  actionTemplateRef = viewChild<TemplateRef<{ $implicit: EmailTemplateListItem }>>('actionTemplate');

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    const t = (key: string): string => this.translocoService.translate(key);

    this.columnConfig.set([
      {
        name: 'name',
        columnType: ColumnType.PLAIN,
        headerLabel: t('dsaManagement.emailTemplates.list.columns.name'),
        cellContent: (row) => t(`dsaManagement.emailTemplates.templates.${row.key}.name`)
      },
      {
        name: 'subject',
        columnType: ColumnType.PLAIN,
        headerLabel: t('dsaManagement.emailTemplates.list.columns.subject'),
        cellContent: (row) => row.subject
      },
      {
        name: 'status',
        columnType: ColumnType.TEMPLATE,
        headerLabel: t('dsaManagement.emailTemplates.list.columns.status'),
        template: this.statusTemplateRef()
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: t('dsaManagement.emailTemplates.list.columns.actions'),
        template: this.actionTemplateRef()
      }
    ]);
  }

  load(): void {
    this.loading.set(true);
    this.emailTemplateService.list().subscribe({
      next: (templates) => {
        this.templates.set(templates);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.showError('dsaManagement.emailTemplates.list.loadingError', 5000);
      }
    });
  }

  edit(template: EmailTemplateListItem): void {
    void this.router.navigate(['admin', 'email-templates', template.key]);
  }

  getTyped(row: unknown): EmailTemplateListItem {
    return row as EmailTemplateListItem;
  }
}
